<template>
  <transition name="fade" mode="out-in">
    <router-view :key="$route.fullPath"/>
  </transition>
  <Loading />
  <Toast />
</template>

<script setup>
import { useStore } from 'vuex'
import { supportedLangs } from '@/config/lang'
import { useRouter, useRoute } from 'vue-router'
import Loading from '@/components/Loading/index.vue'
import { useI18n } from 'vue-i18n'
import { detectLocaleByIP } from '@/utils/ipDetector'
import { allowedAutoRedirectPaths } from '@/router'
import Api from '@/config/api'

const store = useStore()
const router = useRouter()
const route = useRoute()
const { locale: i18nLocale } = useI18n()
const CACHE_KEY = 'user_selected_locale'
const DEFAULT_LOCALE = 'sg' // 默认区域：新加坡

// 缓存区域代码到localStorage
function cacheLocale(locale) {
  if (locale && supportedLangs.includes(locale)) {
    localStorage.setItem(CACHE_KEY, locale)
  }
}

// 构建完整的跳转路径（包含查询参数和hash）
function buildTargetPath(locale, path, query, hash) {
  // 获取 base URL，确保以 / 结尾
  const baseUrl = process.env.VUE_APP_HISTORY_URL || '/'
  const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
  
  // 移除 path 中可能存在的 locale 前缀
  let cleanPath = path
  // 如果 path 的第一个段是支持的 locale，去掉它
  const pathParts = path.split('/').filter(Boolean)
  if (pathParts.length > 0 && supportedLangs.includes(pathParts[0])) {
    // 去掉第一个 locale 段
    pathParts.shift()
    cleanPath = pathParts.length > 0 ? '/' + pathParts.join('/') : '/'
  }
  
  // 构建新的路径：baseUrl + /locale + cleanPath
  const basePath = cleanPath === '/' 
    ? `${normalizedBaseUrl}/${locale}` 
    : `${normalizedBaseUrl}/${locale}${cleanPath}`
  
  const queryString = query && Object.keys(query).length > 0 
    ? '?' + new URLSearchParams(query).toString() 
    : ''
  const hashString = hash || ''
  return basePath + queryString + hashString
}

router.beforeEach(async (to, from, next) => {
  const path = to.path
  const routeLocale = to.params.locale

  // 检查路径是否允许自动跳转（从路由配置中读取）
  // 注意：无 locale 的根路径 `/` 会命中 catch-all NotFound，但仍需走语言自动跳转
  const isAllowedPath = allowedAutoRedirectPaths.includes(path) ||
                        allowedAutoRedirectPaths.some(allowedPath => path.startsWith(allowedPath + '/'))

  // 404 内容直接放行，地址保持为用户打开的路径
  // 允许自动跳转的路径即使命中 NotFound 也不提前放行
  if (path === '/404' || to.name === 'LocaleNotFound' || (to.name === 'NotFound' && !isAllowedPath)) {
    next()
    return
  }
  
  // 如果URL中指定了有效的locale参数，使用它并缓存
  if (routeLocale && supportedLangs.includes(routeLocale)) {
    cacheLocale(routeLocale)
    // 继续原有逻辑
    if (path !== '/404') {
      if (i18nLocale.value !== routeLocale) {
        i18nLocale.value = routeLocale
        console.log(i18nLocale.value, 'i18nLocale.value')
        store.commit('SET_LOCALE', routeLocale)
      }
      next()
      return
    }
  }
  
  // URL中没有locale参数或无效，需要确定区域
  if (!routeLocale || !supportedLangs.includes(routeLocale)) {
    // 只对允许的路径进行自动跳转，其他路径直接404，避免死循环
    if (!isAllowedPath) {
      // 如果路径不在允许列表中，且没有有效的locale，直接404
      next({
        name: 'NotFound',
        params: { pathMatch: path.split('/').filter(Boolean) },
        query: to.query,
        hash: to.hash,
        replace: true
      })
      return
    }
    
    // 1. 优先检查缓存
    const cachedLocale = localStorage.getItem(CACHE_KEY)
    if (cachedLocale && supportedLangs.includes(cachedLocale)) {
      cacheLocale(cachedLocale) // 确保缓存存在
      // 使用 window.location.replace 进行真正的页面跳转，有利于SEO
      const targetPath = buildTargetPath(cachedLocale, path, to.query, to.hash)
      window.location.replace(targetPath)
      return
    }
    
    // 2. 缓存不存在，进行IP检测（3秒超时）
    console.log('缓存不存在，开始IP检测...')
    let detectedLocale = null
    try {
      // detectLocaleByIP 内部已设置3秒超时
      detectedLocale = await detectLocaleByIP()
      console.log('IP检测结果:', detectedLocale)
      
      if (detectedLocale && supportedLangs.includes(detectedLocale)) {
        cacheLocale(detectedLocale) // 缓存检测结果
        // 使用 window.location.replace 进行真正的页面跳转，有利于SEO
        const targetPath = buildTargetPath(detectedLocale, path, to.query, to.hash)
        window.location.replace(targetPath)
        return
      }
    } catch (error) {
      // 超时或其他错误，使用默认区域
      // axios timeout 会抛出错误，这里统一处理
      console.warn('IP detection failed or timeout (3s):', error.message || error)
    }
    
    // 3. IP检测失败或返回的区域不在支持列表中，使用默认区域
    console.log('IP检测失败或超时，使用默认区域:', DEFAULT_LOCALE)
    cacheLocale(DEFAULT_LOCALE) // 缓存默认区域
    // 使用 window.location.replace 进行真正的页面跳转，有利于SEO
    const targetPath = buildTargetPath(DEFAULT_LOCALE, path, to.query, to.hash)
    window.location.replace(targetPath)
    return
  }
  
  // 原有的locale验证逻辑
  if (path !== '/404' && !supportedLangs.includes(routeLocale)) {
    next({
      name: 'NotFound',
      params: { pathMatch: path.split('/').filter(Boolean) },
      query: to.query,
      hash: to.hash,
      replace: true
    })
    return
  }
  
  // 原有逻辑继续...
  if (i18nLocale.value !== routeLocale) {
    i18nLocale.value = routeLocale
    console.log(i18nLocale.value, 'i18nLocale.value')
    store.commit('SET_LOCALE', routeLocale)
  }
  next()
})

const urlLogin = async () => {
  const query = route.query
  console.log(query, 'query')
  console.log('urlLogin uid', route.query)
  if (query.uid && !query.tokenHash) {
    try {
      const res = await Api.getCpUserInfo({
        cpUserId: query.uid
      }, { showToast: false })
      if (res.cpUserId) {
        store.commit('SET_CP_USER_INFO', res)
        router.replace({
          query: {
            uid: undefined
          }
        })
      }
    } catch (error) {
      console.error(error)
      router.replace({
        query: {
          uid: undefined
        }
      })
    }
  }
}

// 等路由完成初始导航后再读 query，否则首次加载时 route.query 可能还是空对象
router.isReady().then(() => {
  urlLogin()
})

</script>

<style lang="less">
// 确保 transition 容器有正确的定位
</style>
